/**
 * Finest Plastering — theme.js
 * Header scroll behaviour, scroll reveal, testimonial rotator,
 * hero scroll cue, coverage calculator.
 * Vanilla JS — no dependencies.
 */

'use strict';

/* =============================================================================
   1. HEADER — Transparent ↔ Solid on scroll
   ============================================================================= */

(function initHeader() {
  const header = document.querySelector('header-component');
  if (!header) return;

  const SCROLL_THRESHOLD = 80;
  let lastScrollY = window.scrollY;
  let ticking = false;

  function updateHeader() {
    const scrollY = window.scrollY;
    const isOverHero = scrollY < SCROLL_THRESHOLD;

    if (isOverHero) {
      header.setAttribute('data-transparent', 'true');
      header.removeAttribute('data-scrolled');
    } else {
      header.removeAttribute('data-transparent');
      header.setAttribute('data-scrolled', 'true');
    }

    lastScrollY = scrollY;
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(updateHeader);
      ticking = true;
    }
  }, { passive: true });

  // Set initial state
  updateHeader();
})();

/* =============================================================================
   2. HERO — Hide scroll cue on scroll
   ============================================================================= */

(function initHeroScrollCue() {
  const cue = document.querySelector('.fp-hero__scroll-cue');
  if (!cue) return;

  let ticking = false;

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        if (window.scrollY > 60) {
          cue.classList.add('is-hidden');
        } else {
          cue.classList.remove('is-hidden');
        }
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });
})();

/* =============================================================================
   3. SCROLL REVEAL — IntersectionObserver
   ============================================================================= */

(function initScrollReveal() {
  const revealElements = document.querySelectorAll('.fp-reveal');
  if (!revealElements.length) return;

  // Respect reduced motion preference
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    revealElements.forEach(el => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -40px 0px'
    }
  );

  revealElements.forEach(el => observer.observe(el));

  // Stagger grid children
  const staggerContainers = document.querySelectorAll('.fp-reveal-stagger');
  staggerContainers.forEach(container => {
    const children = container.children;
    Array.from(children).forEach((child, i) => {
      child.style.transitionDelay = `${i * 80}ms`;
      child.classList.add('fp-reveal');
      observer.observe(child);
    });
  });
})();

/* =============================================================================
   4. LUXURY TESTIMONIALS — Auto-rotating carousel
   ============================================================================= */

(function initLuxuryTestimonials() {
  const sliders = document.querySelectorAll('.fp-luxury-testimonials__slider');
  
  sliders.forEach(function(slider) {
    const track = slider.querySelector('.fp-luxury-testimonials__track');
    const dots = slider.querySelectorAll('.fp-luxury-testimonials__dot');
    const prevBtn = slider.querySelector('.fp-luxury-testimonials__btn--prev');
    const nextBtn = slider.querySelector('.fp-luxury-testimonials__btn--next');
    
    if (!track) return;
    
    var currentIndex = 0;
    var autoplayInterval = null;
    var INTERVAL = 5000;
    var totalSlides = dots.length;
    
    function goToSlide(index) {
      if (dots[currentIndex]) dots[currentIndex].classList.remove('is-active');
      
      currentIndex = index;
      
      track.style.transform = 'translateX(-' + (currentIndex * 100) + '%)';
      if (dots[currentIndex]) dots[currentIndex].classList.add('is-active');
    }
    
    function nextSlide() {
      goToSlide((currentIndex + 1) % totalSlides);
    }
    
    function prevSlide() {
      goToSlide((currentIndex - 1 + totalSlides) % totalSlides);
    }
    
    function startAutoplay() {
      clearInterval(autoplayInterval);
      autoplayInterval = setInterval(nextSlide, INTERVAL);
    }
    
    if (prevBtn) {
      prevBtn.addEventListener('click', function() {
        prevSlide();
        startAutoplay();
      });
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', function() {
        nextSlide();
        startAutoplay();
      });
    }
    
    dots.forEach(function(dot, index) {
      dot.addEventListener('click', function() {
        goToSlide(index);
        startAutoplay();
      });
    });
    
    slider.addEventListener('mouseenter', function() {
      clearInterval(autoplayInterval);
    });
    
    slider.addEventListener('mouseleave', startAutoplay);
    
    goToSlide(0);
    startAutoplay();
  });
})();

/* =============================================================================
   5. LEGACY TESTIMONIALS — Auto-rotating carousel (old)
   ============================================================================= */

(function initTestimonials() {
  const container = document.querySelector('.fp-testimonials');
  if (!container) return;

  const slides    = container.querySelectorAll('.fp-testimonials__slide');
  const dots      = container.querySelectorAll('.fp-testimonials__dot');
  const prevBtn   = container.querySelector('.fp-testimonials__arrow--prev');
  const nextBtn   = container.querySelector('.fp-testimonials__arrow--next');

  if (!slides.length) return;

  let current = 0;
  let timer = null;
  const INTERVAL = 8000;

  function goTo(index) {
    slides[current].classList.remove('is-active');
    if (dots[current]) dots[current].classList.remove('is-active');

    current = (index + slides.length) % slides.length;

    slides[current].classList.add('is-active');
    if (dots[current]) dots[current].classList.add('is-active');
  }

  function startTimer() {
    clearInterval(timer);
    timer = setInterval(() => goTo(current + 1), INTERVAL);
  }

  function advance(dir) {
    goTo(current + dir);
    startTimer();
  }

  if (prevBtn) prevBtn.addEventListener('click', () => advance(-1));
  if (nextBtn) nextBtn.addEventListener('click', () => advance(1));

  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      goTo(i);
      startTimer();
    });
  });

  container.addEventListener('mouseenter', () => clearInterval(timer));
  container.addEventListener('mouseleave', startTimer);

  goTo(0);
  startTimer();
})();

/* =============================================================================
   5. COVERAGE CALCULATOR — Custom Element
   ============================================================================= */

class CoverageCalculator extends HTMLElement {
  connectedCallback() {
    this._sqmPerKg = parseFloat(this.dataset.sqmPerKg) || 0;
    this._lengthInput = this.querySelector('#fp-calc-length');
    this._widthInput  = this.querySelector('#fp-calc-width');
    this._areaDisplay = this.querySelector('#fp-calc-area');
    this._resultEl    = this.querySelector('.fp-coverage-calc__result');
    this._resultStrong = this.querySelector('#fp-calc-result');

    if (!this._lengthInput || !this._widthInput) return;

    this._lengthInput.addEventListener('input', () => this._calculate());
    this._widthInput.addEventListener('input',  () => this._calculate());
  }

  _calculate() {
    const length = parseFloat(this._lengthInput.value) || 0;
    const width  = parseFloat(this._widthInput.value)  || 0;
    const area   = length * width;

    if (this._areaDisplay) {
      this._areaDisplay.textContent = area > 0 ? area.toFixed(2) : '—';
    }

    if (area > 0 && this._sqmPerKg > 0) {
      const kgRaw       = area / this._sqmPerKg;
      const kgWastage   = kgRaw * 1.1;          // +10% wastage
      const kgDisplay   = kgWastage.toFixed(1);

      if (this._resultStrong) {
        this._resultStrong.textContent = `${kgDisplay} kg`;
      }
      if (this._resultEl) {
        this._resultEl.classList.remove('fp-hidden');
      }
    } else {
      if (this._resultEl) {
        this._resultEl.classList.add('fp-hidden');
      }
    }
  }
}

if (!customElements.get('coverage-calculator')) {
  customElements.define('coverage-calculator', CoverageCalculator);
}

/* =============================================================================
   6. QUICK ADD — Prevent double-clicks
   ============================================================================= */

(function initQuickAdd() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.fp-product-card__quick-add');
    if (!btn) return;

    if (btn.dataset.loading === 'true') {
      e.preventDefault();
      return;
    }

    btn.dataset.loading = 'true';
    const original = btn.textContent;
    btn.textContent = 'Adding…';

    // Re-enable after 2s (Horizon handles the actual add-to-cart)
    setTimeout(() => {
      btn.dataset.loading = 'false';
      btn.textContent = original;
    }, 2000);
  });
})();

/* =============================================================================
   7. CROSS-SELL — Add to cart from cross-sell cards
   ============================================================================= */

(function initCrossSell() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.fp-cross-sell__add');
    if (!btn) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    if (btn.disabled) return;
    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = '✓';

    try {
      await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: variantId, quantity: 1 })
      });

      // Trigger Horizon's cart refresh
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (err) {
      console.error('Cross-sell add failed:', err);
    } finally {
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = original;
      }, 1500);
    }
  });
})();

/* =============================================================================
   8. COLOUR SWATCH FILTER (Colour Booklet page)
   ============================================================================= */

(function initSwatchFilter() {
  const filterBar = document.querySelector('.fp-swatch-filter');
  if (!filterBar) return;

  const buttons = filterBar.querySelectorAll('[data-filter]');
  const swatches = document.querySelectorAll('[data-family]');

  buttons.forEach(btn => {
    btn.addEventListener('click', () => {
      const filter = btn.dataset.filter;

      // Update active state
      buttons.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      // Show / hide swatches
      swatches.forEach(swatch => {
        if (filter === 'all' || swatch.dataset.family === filter) {
          swatch.style.display = '';
        } else {
          swatch.style.display = 'none';
        }
      });
    });
  });
})();
